import type { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, Flex, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { productAdjustmentSchema } from "../schemas/productAdjustmentSchema";
import ProductAdjustmentForm from "./ProductAdjustmentForm";
import { CreateProductAdjustment } from "../types/ProductAdjustment";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";
import { ProductState } from "@/core/products/components/ProductsTable";
import AdjustmentProductsTable from "./AdjustmentProductsTable";
import { findAdjustmentIssue } from "../utils/adjustmentStock";
import { ProductAdjustment } from "@/shared/core-product-adjustments";
import { getDefaultValues } from "../consts";
import { useEditProductAdjustment } from "../useCases/useEditProductAdjustment";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { toast } from "@/ui/chakra/toaster";
import { useAuth } from "@/shared/session";

type ProductAdjustmentFormInput = z.infer<typeof productAdjustmentSchema>;

interface EditProductAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultValues: ProductAdjustment;
}

export const EditProductAdjustmentModal = ({
  isOpen,
  onClose,
  defaultValues,
}: EditProductAdjustmentModalProps) => {
  const [searchProductModal, setSearchProductModal] = useState(false);
  const { session } = useAuth();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ProductAdjustmentFormInput>({
      resolver: zodResolver(productAdjustmentSchema),
      defaultValues: getDefaultValues(
        defaultValues,
        session?.zone ?? undefined,
      ),
    });

  const { mutate, isPending } = useEditProductAdjustment();

  const { products, storageId } = watch();

  useEffect(() => {
    if (isOpen) {
      reset(getDefaultValues(defaultValues, session?.zone ?? undefined));
    }
  }, [isOpen, reset, defaultValues, session?.zone]);

  const handleAddProduct = (product: ProductState) => {
    if (products.some((p) => p.data.id === product.data.id)) return;

    setValue("products", [...products, product]);
  };

  const handleEditProductAdjustment = (data: ProductAdjustmentFormInput) => {
    if (data.products.length === 0) {
      toast({
        title: "Error al editar el ajuste de producto.",
        description: "Debe agregar al menos un producto al ajuste de producto.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const issue = findAdjustmentIssue(data.products);
    if (issue) {
      toast({
        title: "Revisa las cantidades",
        description: issue,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const formattedData: Partial<CreateProductAdjustment> = {
      productAdjustmentTypeId: data.productAdjustmentTypeId,
      storageId: data.storageId,
      description: data.description,
      products: data.products.map((product) => ({
        id: product.data.id,
        quantity: product.amount,
      })),
    };
    mutate(
      { id: defaultValues.id, productAdjustmentData: formattedData },
      {
        onSuccess: () => {
          onClose();
          toast({
            title: "Ajuste de producto actualizado.",
            description:
              "El ajuste de producto se ha actualizado exitosamente.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        },
        onError: (error) => {
          console.error("Error al editar el ajuste de producto", error);
          toast({
            title: "Error al editar el ajuste de producto.",
            description:
              error.response?.data.message || "Ocurrió un error inesperado.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        },
      },
    );
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <DialogContent>
        <form onSubmit={handleSubmit(handleEditProductAdjustment)}>
          <DialogHeader borderBottomWidth="1px" borderColor="border">
            <DialogTitle>Editar reajuste de producto</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody display="flex" flexDirection="column" gap={6} pt={5}>
            <ProductAdjustmentForm control={control} />

            <Flex align="center" justify="space-between" gap={4} wrap="wrap">
              <Text fontWeight="semibold">
                Productos{" "}
                <Text as="span" color="fg.muted" fontWeight="normal">
                  · {products.length}
                </Text>
              </Text>
              <Button
                variant="outline"
                fontWeight="normal"
                color="fg.muted"
                justifyContent="flex-start"
                w={{ base: "full", sm: "320px" }}
                onClick={() => setSearchProductModal(true)}
                type="button"
              >
                <LuSearch />
                Buscar producto...
              </Button>
            </Flex>
            <AdjustmentProductsTable
              products={products}
              setProducts={(newProducts) => setValue("products", newProducts)}
            />
            <ModalSearchProduct
              isOpen={searchProductModal}
              onClose={() => setSearchProductModal(false)}
              selected={products}
              onChange={handleAddProduct}
              productType="all"
              storageId={storageId}
            />
          </DialogBody>
          <DialogFooter borderTopWidth="1px" borderColor="border">
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
